/**
 * Authenticity verification (THEME.md §7.13; authenticity verification spec V1, V2, V6, V13, V14, V16, V18;
 * D-75 to D-77; T-91): the element of `blocks/okt-verification.liquid`.
 *
 * - The serial field formats as you type (capitals, a dash after the third and sixth character), checks the
 *   format, then asks the verification service (theme setting `okt_verification_url`) with a GET and `?serial=`.
 *   200 shows the item, 404 "not found", 400 "wrong format"; any other answer, no connection or no answer
 *   within 8 s "unavailable", with "Try again". An answer encoded twice (a JSON string holding the JSON) is
 *   accepted. While it checks, the field is read-only and Verify shows a spinner.
 * - A verified item opens in Horizon's dialog (`dialog-component` with `dialog-modal`, as quick add), or shows
 *   in place of the form (V18). Focus goes to the result title. When the dialog closes (Escape, the close button,
 *   a click outside or "Verify another item"), focus returns to the serial field.
 * - Photos (V16, D-75 to D-77): `snippets/okt-verification-gallery.liquid` renders Horizon's carousel for one
 *   prototype photo in a template. The clone gets one slide and one control item per photo before it enters the
 *   page, so Horizon's own components (slideshow-component, slideshow-controls, scroll-hint) and the Oktagon
 *   progress bar set themselves up as on a product page. The service's `gallery` repeats `image`; the repeat is
 *   dropped. Shopify CDN photos get a srcset from the CDN's `width` parameter. With the Auto ratio, the result
 *   shows once its first photo has loaded and every photo's ratio is known, so nothing jumps; after 3 s it shows
 *   anyway (D-76).
 * - The serial is only sent to the service: never into a link, the page address, storage or an event (V6).
 * - Theme editor (V13): while the block is selected, the sample result (rendered in design mode only) shows; it
 *   goes when the block is deselected, and comes back when a setting change renders the block again.
 */
import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/** Three letters or digits, three letters or digits, five digits */
const SERIAL_FORMAT = /^[A-Z0-9]{3}-[A-Z0-9]{3}-\d{5}$/;
const SERIAL_LENGTH = 11;
const TIMEOUT = 8000;
/** With the Auto ratio, the longest wait for the photos before the result shows anyway (D-76) */
const PHOTO_WAIT = 3000;

/** Horizon's product photo widths up to what the result's columns need (`snippets/product-media.liquid`) */
const PHOTO_WIDTHS = [240, 352, 640, 832, 1200, 1600];
/** Horizon's thumbnail width (`snippets/slideshow-controls.liquid`); also the size that tells a photo's ratio */
const THUMBNAIL_WIDTH = 144;

/** Blocks selected in the theme editor, so a block rendered again after a setting change shows its sample */
const selectedBlocks = new Set();

/** @typedef {'empty' | 'format' | 'notFound' | 'unavailable'} ErrorKind */

class VerificationError extends Error {
  /** @param {ErrorKind} kind */
  constructor(kind) {
    super(kind);
    this.kind = kind;
  }
}

/**
 * @typedef {object} VerifiedItem
 * @property {string} title
 * @property {string} [serial]
 * @property {string} [autograph]
 * @property {string} [about]
 * @property {string} [date]
 * @property {string} [image]
 * @property {string[]} [gallery]
 */

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} [form]
 * @property {HTMLInputElement} [input]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [errorText]
 * @property {HTMLButtonElement} [retry]
 * @property {HTMLElement} [hint]
 * @property {HTMLElement} [status]
 * @property {HTMLTemplateElement} [resultTemplate]
 * @property {HTMLElement} [inline]
 * @property {import('./dialog').DialogComponent} [dialogComponent]
 *
 * @extends {Component<Refs>}
 */
class OktVerification extends Component {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #pending = null;

  /** Counts the results being prepared, so only the latest one shows */
  #showing = 0;

  /** The result on show is the editor sample */
  #sampleShown = false;

  /** The dialog closes because the block was deselected in the editor: focus stays */
  #quietClose = false;

  connectedCallback() {
    super.connectedCallback();

    // Without the serial field the block is static (details and link)
    if (!this.refs.form) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });
    this.refs.dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    this.refs.dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    // Theme editor: a setting change renders the block again; while it is selected, the sample comes back
    if (window.Shopify?.designMode && selectedBlocks.has(this.#blockId)) {
      requestAnimationFrame(() => this.#showSample());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#pending?.abort();
  }

  get #blockId() {
    return this.dataset.blockId ?? '';
  }

  get #popup() {
    return this.dataset.result !== 'inline';
  }

  /**
   * Capitals, and a dash after the third and sixth character, keeping the caret after the same character (on:input)
   */
  format() {
    const { input } = this.refs;
    if (!input) return;

    const caret = input.selectionStart ?? input.value.length;
    const before = this.#characters(input.value.slice(0, caret)).length;
    const characters = this.#characters(input.value).slice(0, SERIAL_LENGTH);
    const value = [characters.slice(0, 3), characters.slice(3, 6), characters.slice(6)].filter(Boolean).join('-');

    if (input.value !== value) {
      input.value = value;
      const position = Math.min(before + (before > 3 ? 1 : 0) + (before > 6 ? 1 : 0), value.length);
      if (document.activeElement === input) input.setSelectionRange(position, position);
    }

    this.#clearError();
  }

  /**
   * Checks the format, then asks the service (on:submit)
   * @param {SubmitEvent} event
   */
  async verify(event) {
    event.preventDefault();

    const { input } = this.refs;
    if (!input) return;

    const serial = input.value.trim().toUpperCase();
    if (!serial) return this.#showError('empty');
    if (!SERIAL_FORMAT.test(serial)) return this.#showError('format');

    this.#pending?.abort();
    const controller = new AbortController();
    this.#pending = controller;
    const timer = setTimeout(() => controller.abort(), TIMEOUT);

    this.#clearError();
    this.#setBusy(true);

    try {
      const item = await this.#request(serial, controller.signal);
      if (this.#pending !== controller) return;

      await this.#show(item);
    } catch (error) {
      if (this.#pending !== controller) return;

      this.#showError(error instanceof VerificationError ? error.kind : 'unavailable');
    } finally {
      clearTimeout(timer);
      if (this.#pending === controller) {
        this.#pending = null;
        this.#setBusy(false);
      }
    }
  }

  /**
   * "Try again" after the service was unavailable (on:click)
   */
  retry() {
    this.refs.form?.requestSubmit();
  }

  /**
   * "Verify another item": the result goes, the field is cleared and takes focus (on:click)
   */
  again() {
    const { input } = this.refs;
    if (!input) return;

    input.value = '';
    this.#clearError();

    if (this.#popup) {
      // The close handler moves focus to the field
      this.refs.dialogComponent?.closeDialog();
    } else {
      this.#hideInline();
      input.focus();
    }
  }

  /**
   * @param {string} value
   */
  #characters(value) {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  /**
   * @param {string} serial
   * @param {AbortSignal} signal
   * @returns {Promise<VerifiedItem>}
   */
  async #request(serial, signal) {
    const url = new URL(this.dataset.service ?? '', window.location.href);
    url.searchParams.set('serial', serial);

    const response = await fetch(url, { signal });
    if (response.status === 404) throw new VerificationError('notFound');
    if (response.status === 400) throw new VerificationError('format');
    if (!response.ok) throw new VerificationError('unavailable');

    /** @type {unknown} */
    let data = JSON.parse(await response.text());
    // Encoded twice: a JSON string that holds the JSON (the live script accepts it too)
    if (typeof data === 'string') data = JSON.parse(data);

    const item = /** @type {{ verifiedProduct?: VerifiedItem } | null} */ (data)?.verifiedProduct;
    if (!item || typeof item !== 'object' || !item.title) throw new VerificationError('unavailable');

    return item;
  }

  /**
   * Opens the dialog with the item, or shows it in place of the form
   * @param {VerifiedItem} item
   * @param {boolean} [sample]
   */
  async #show(item, sample = false) {
    const showing = ++this.#showing;
    const { dialogComponent, inline, form } = this.refs;
    const slot = this.#popup ? dialogComponent?.querySelector('[data-okt-verification-slot]') : inline;
    if (!slot || !form) return;

    const result = this.#buildResult(item, sample);

    // In place while still hidden, so the first photo starts loading
    if (!this.#popup) this.#hideInline();
    slot.replaceChildren(result);
    if (this.dataset.aspectRatio === 'adapt') await this.#waitForPhotos(result);
    if (showing !== this.#showing) return;

    this.#sampleShown = sample;
    this.#setStatus('');

    if (!this.#popup) {
      slot.hidden = false;
      form.hidden = true;
      this.#title?.focus();
      return;
    }

    if (dialogComponent?.refs.dialog?.open) {
      this.#title?.focus();
    } else {
      dialogComponent?.showDialog();
    }
  }

  #hideInline() {
    const { inline, form } = this.refs;
    if (!inline || !form) return;

    inline.replaceChildren();
    inline.hidden = true;
    form.hidden = false;
    this.#sampleShown = false;
  }

  /** @returns {HTMLElement | null} */
  get #title() {
    return this.querySelector('.okt-verification__title');
  }

  #handleDialogOpen = () => {
    this.#title?.focus();
  };

  #handleDialogClose = () => {
    this.#sampleShown = false;

    if (this.#quietClose) {
      this.#quietClose = false;
      return;
    }

    this.refs.input?.focus();
  };

  #handleBlockSelect = () => {
    selectedBlocks.add(this.#blockId);
    this.#showSample();
  };

  #handleBlockDeselect = () => {
    selectedBlocks.delete(this.#blockId);
    if (!this.#sampleShown) return;

    if (!this.#popup) {
      this.#hideInline();
      return;
    }

    const { dialogComponent } = this.refs;
    if (!dialogComponent?.refs.dialog?.open) return;

    this.#quietClose = true;
    dialogComponent.closeDialog();
  };

  #showSample() {
    const sample = this.#sample();
    if (sample) this.#show(sample, true);
  }

  /**
   * The editor sample: its text, and Shopify's placeholder drawings as photos
   * @returns {VerifiedItem | null}
   */
  #sample() {
    const template = this.querySelector('template[data-okt-verification-sample]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const json = template.content.querySelector('script[type="application/json"]')?.textContent ?? '';
    const serializer = new XMLSerializer();
    const photos = [...template.content.querySelectorAll('svg')].map(
      (svg) => `data:image/svg+xml,${encodeURIComponent(serializer.serializeToString(svg))}`
    );

    try {
      return { ...JSON.parse(json), image: photos[0], gallery: photos };
    } catch {
      return null;
    }
  }

  /**
   * @param {VerifiedItem} item
   * @param {boolean} sample
   * @returns {HTMLElement}
   */
  #buildResult(item, sample) {
    const template = /** @type {HTMLTemplateElement} */ (this.refs.resultTemplate);
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const result = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const title = String(item.title).trim();

    /**
     * @param {string} name
     * @param {unknown} value
     */
    const fill = (name, value) => {
      const text = typeof value === 'string' ? value.trim() : '';
      const row = result.querySelector(`[data-okt-row="${name}"]`);
      const field = result.querySelector(`[data-okt-field="${name}"]`);

      if (row instanceof HTMLElement) row.hidden = !text;
      if (field) field.textContent = text;
    };

    fill('title', title);
    fill('serial', item.serial != null ? String(item.serial) : '');
    fill('autograph', item.autograph);
    fill('date', this.#formatDate(item.date));
    fill('about', item.about);

    // In the White popover the title gets the static highlighter (THEME.md §7.13)
    if (this.#popup) result.querySelector('[data-okt-field="title"]')?.classList.add('okt-highlight');
    result.querySelector('[data-okt-sample]')?.toggleAttribute('hidden', !sample);

    // The service's gallery starts with the main photo again: each photo once
    const photos = [...new Set([item.image, ...(Array.isArray(item.gallery) ? item.gallery : [])])].filter(
      (photo) => typeof photo === 'string' && photo !== ''
    );
    const slot = result.querySelector('[data-okt-verification-photos]');
    const gallery = photos.length ? this.#buildGallery(/** @type {string[]} */ (photos), title) : null;

    if (gallery) {
      slot?.replaceWith(gallery);
    } else {
      slot?.remove();
    }

    return result;
  }

  /**
   * Horizon's gallery from the block's template, with a slide and a control item for every photo
   * @param {string[]} photos
   * @param {string} title
   * @returns {HTMLElement | null}
   */
  #buildGallery(photos, title) {
    const template = this.querySelector('template[data-okt-verification-gallery]');
    const thumbnail = this.querySelector('template[data-okt-verification-thumbnail]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const root = /** @type {HTMLElement | null} */ (template.content.firstElementChild?.cloneNode(true) ?? null);
    const slideshow = root?.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');
    const prototype = scroller?.querySelector('slideshow-slide');
    if (!root || !(slideshow instanceof HTMLElement) || !scroller || !(prototype instanceof HTMLElement)) return null;

    const { length } = photos;
    const status = template.dataset.slideStatus ?? '';
    const width = this.#photoWidth();
    /** @param {number} index */
    const slideLabel = (index) => status.replace('[index]', String(index + 1)).replace('[length]', String(length));
    /** @param {number} index */
    const photoAlt = (index) =>
      (this.dataset.photoAlt ?? '')
        .replace('[title]', title)
        .replace('[index]', String(index + 1))
        .replace('[length]', String(length));

    photos.forEach((photo, index) => {
      const slide = /** @type {HTMLElement} */ (prototype.cloneNode(true));
      slide.setAttribute('aria-hidden', String(index > 0));
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
      slide.dataset.oktPhoto = photo;

      const image = slide.querySelector('img');
      if (image) this.#setPhoto(image, photo, photoAlt(index), { first: index === 0, width });

      scroller.append(slide);
    });

    prototype.remove();
    slideshow.style.setProperty('--slideshow-timeline', photos.map((_, index) => `--slide-${index}`).join(', '));

    // One photo: no arrows or controls (Horizon)
    if (length < 2) {
      slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
      for (const element of root.querySelectorAll('slideshow-arrows, slideshow-controls, okt-slider-progress')) {
        element.remove();
      }
      return root;
    }

    for (const controls of root.querySelectorAll('slideshow-controls')) {
      const thumbnails = controls.querySelector('.slideshow-controls__thumbnails');
      const button = thumbnail instanceof HTMLTemplateElement ? thumbnail.content.firstElementChild : null;

      if (thumbnails && button) {
        photos.forEach((photo, index) => {
          const item = /** @type {HTMLElement} */ (button.cloneNode(true));
          item.setAttribute('on:click', `/select/${index}`);
          item.setAttribute('aria-label', slideLabel(index));
          if (index === 0) item.setAttribute('aria-selected', 'true');

          const image = item.querySelector('img');
          if (image) image.src = this.#photoUrl(photo, THUMBNAIL_WIDTH);

          thumbnails.append(item);
        });
      }

      const dots = controls.querySelector('.slideshow-controls__dots');
      const dot = dots?.firstElementChild;

      if (dots && dot) {
        photos.forEach((_, index) => {
          const item = index === 0 ? dot : /** @type {HTMLElement} */ (dot.cloneNode(true));
          const control = item.querySelector('button');
          if (control) {
            control.setAttribute('on:click', `/select/${index}`);
            control.setAttribute('aria-label', slideLabel(index));
            control.style.setProperty('animation-timeline', `--slide-${index}`);
            if (index === 0) {
              control.setAttribute('aria-selected', 'true');
            } else {
              control.removeAttribute('aria-selected');
            }
            control.textContent = String(index + 1);
          }
          if (index > 0) dots.append(item);
        });
      }

      const counter = controls.querySelector('.slideshow-controls__counter');
      if (counter?.lastChild?.nodeType === Node.TEXT_NODE) counter.lastChild.textContent = String(length);
    }

    return root;
  }

  /**
   * The width the photos take, for the first photo's sizes: half the dialog's or the block's content from 750 px,
   * all of it on phones
   * @returns {number}
   */
  #photoWidth() {
    const wide = window.matchMedia('(min-width: 750px)').matches;
    let content = (this.refs.inline?.parentElement ?? this).getBoundingClientRect().width;

    if (this.#popup) {
      const dialog = this.refs.dialogComponent?.refs.dialog;
      const style = dialog ? getComputedStyle(dialog) : null;
      const maxWidth = style?.maxWidth.endsWith('px') ? parseFloat(style.maxWidth) : window.innerWidth;
      const padding = style ? parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) : 0;
      content = Math.min(maxWidth, window.innerWidth) - (Number.isFinite(padding) ? padding : 0);
    }

    return Math.max(1, Math.ceil(wide ? content / 2 : content));
  }

  /**
   * A responsive photo: a srcset from the Shopify CDN's `width` parameter. The first photo loads at once, the
   * others when they come into view.
   * @param {HTMLImageElement} image
   * @param {string} photo
   * @param {string} alt
   * @param {{ first: boolean, width: number }} options
   */
  #setPhoto(image, photo, alt, { first, width }) {
    const url = this.#cdnUrl(photo);

    image.alt = alt;
    if (first) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }

    if (url) {
      image.sizes = first ? `${width}px` : `auto, ${width}px`;
      image.srcset = PHOTO_WIDTHS.map((size) => `${this.#withWidth(url, size)} ${size}w`).join(', ');
      image.src = this.#withWidth(url, PHOTO_WIDTHS[PHOTO_WIDTHS.length - 1] ?? 1600);
    } else {
      image.src = photo;
    }
  }

  /**
   * Auto ratio (D-76): every photo's ratio comes from its thumbnail-sized copy (the thumbnails' own address), and
   * the first photo loads, before the result shows; after PHOTO_WAIT it shows anyway
   * @param {HTMLElement} result
   */
  async #waitForPhotos(result) {
    const slides = /** @type {HTMLElement[]} */ ([...result.querySelectorAll('slideshow-slide[data-okt-photo]')]);
    const thumbnails = [...result.querySelectorAll('.slideshow-controls__thumbnail')];

    const ratios = slides.map(
      (slide, index) =>
        new Promise((resolve) => {
          const probe = new Image();
          const done = () => {
            if (probe.naturalWidth && probe.naturalHeight) {
              const ratio = String(probe.naturalWidth / probe.naturalHeight);
              slide.style.setProperty('--media-preview-ratio', ratio);
              slide.querySelector('.product-media')?.style.setProperty('--ratio', ratio);
              for (const button of thumbnails) {
                if (button.getAttribute('on:click') === `/select/${index}`) {
                  /** @type {HTMLElement} */ (button).style.setProperty('--aspect-ratio', ratio);
                }
              }
            }
            resolve(undefined);
          };
          probe.addEventListener('load', done, { once: true });
          probe.addEventListener('error', done, { once: true });
          probe.src = this.#photoUrl(slide.dataset.oktPhoto ?? '', THUMBNAIL_WIDTH);
        })
    );

    const first = slides[0]?.querySelector('img');
    const firstLoaded =
      first && !first.complete
        ? new Promise((resolve) => {
            first.addEventListener('load', resolve, { once: true });
            first.addEventListener('error', resolve, { once: true });
          })
        : Promise.resolve();

    // Until a ratio is known, the slides are square
    for (const slide of slides) slide.style.setProperty('--media-preview-ratio', '1');

    await Promise.race([
      Promise.all([...ratios, firstLoaded]),
      new Promise((resolve) => setTimeout(resolve, PHOTO_WAIT)),
    ]);
  }

  /**
   * @param {string} photo
   * @param {number} width
   */
  #photoUrl(photo, width) {
    const url = this.#cdnUrl(photo);
    return url ? this.#withWidth(url, width) : photo;
  }

  /**
   * The photo's address when the Shopify CDN can resize it
   * @param {string} photo
   * @returns {URL | null}
   */
  #cdnUrl(photo) {
    try {
      const url = new URL(photo, window.location.href);
      if (url.protocol !== 'https:') return null;
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) return url;
    } catch {
      // Not an address
    }
    return null;
  }

  /**
   * @param {URL} url
   * @param {number} width
   */
  #withWidth(url, width) {
    const sized = new URL(url);
    sized.searchParams.set('width', String(width));
    return sized.href;
  }

  /**
   * The signing date in the page's language, when the service sends one (V14)
   * @param {unknown} value
   */
  #formatDate(value) {
    if (typeof value !== 'string') return '';

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || undefined, { dateStyle: 'long' }).format(date);
    } catch {
      return value;
    }
  }

  /**
   * Checking: the field is read-only, Verify shows a spinner, and the live region says so
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { form, input } = this.refs;
    if (!form || !input) return;

    input.readOnly = busy;
    form.setAttribute('aria-busy', String(busy));
    if (busy) this.#setStatus(this.dataset.checking ?? '');
  }

  /**
   * @param {string} text
   */
  #setStatus(text) {
    const { status } = this.refs;
    if (status && status.textContent !== text) status.textContent = text;
  }

  /**
   * The message under the field, linked to it; "Try again" when the service is unavailable
   * @param {ErrorKind} kind
   */
  #showError(kind) {
    const { input, error, errorText, retry, hint } = this.refs;
    if (!input || !error || !errorText) return;

    const template = this.querySelector(`template[data-okt-verification-message="${kind}"]`);
    errorText.replaceChildren(template instanceof HTMLTemplateElement ? template.content.cloneNode(true) : '');

    error.hidden = false;
    if (retry) retry.hidden = kind !== 'unavailable';
    if (hint) hint.hidden = true;
    input.setAttribute('aria-invalid', 'true');
    this.#setStatus(errorText.textContent?.trim() ?? '');
  }

  #clearError() {
    const { input, error, errorText, retry, hint } = this.refs;
    if (!input || !error || !errorText) return;

    error.hidden = true;
    errorText.replaceChildren();
    if (retry) retry.hidden = true;
    if (hint) hint.hidden = false;
    input.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('okt-verification')) {
  customElements.define('okt-verification', OktVerification);
}
